import { zodResolver } from '@hookform/resolvers/zod';
import {
  configMgmtControllerAttachDocumentType,
  configMgmtControllerListAllDocumentTypes,
  configMgmtControllerRemoveDocumentLink,
  configMgmtControllerUpdateDocumentLink,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Pencil, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../components/ui/button';
import { DataTable, type DataTableColumn } from '../../../../components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { usePermissions } from '../../../../hooks/use-permissions';
import { useAuthStore } from '../../../../stores/auth.store';
import { useDocumentsPageStore } from '../../../../stores/config/documents-page.store';
import { PermitTypeShell } from './components/PermitTypeShell';
import type { DocumentTypeLink, PermitTypeDetail } from './components/types';

export const Route = createFileRoute('/config/request-types/$requestTypeId/documents')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('config:view')) throw redirect({ to: '/dashboard' });
  },
  component: DocumentsPage,
});

interface MasterDocType {
  id: string;
  name: string;
  code: string;
  status: string;
}

const attachSchema = z.object({
  documentTypeId: z.string().min(1, 'Select a document type'),
  isMandatory: z.boolean(),
  requestType: z.string(),
});
const editLinkSchema = z.object({
  isMandatory: z.boolean(),
  requestType: z.string(),
  status: z.string(),
});

function DocumentsTab({ permitType }: { permitType: PermitTypeDetail }) {
  const { can } = usePermissions();
  const qc = useQueryClient();
  const canEdit = can('config:edit');

  const {
    addOpen,
    editTarget: _editTarget,
    deleteConfirm,
    deleteError,
    setAddOpen,
    setEditTarget,
    setDeleteConfirm,
    setDeleteError,
  } = useDocumentsPageStore();
  const editTarget = _editTarget as DocumentTypeLink | null;

  const attachForm = useForm<z.infer<typeof attachSchema>>({
    resolver: zodResolver(attachSchema),
    defaultValues: { documentTypeId: '', isMandatory: true, requestType: '' },
  });
  const editForm = useForm<z.infer<typeof editLinkSchema>>({
    resolver: zodResolver(editLinkSchema),
    defaultValues: { isMandatory: true, requestType: '', status: 'ACTIVE' },
  });

  useEffect(() => {
    if (editTarget)
      editForm.reset({
        isMandatory: editTarget.isMandatory as boolean,
        requestType: (editTarget.requestType as string) ?? '',
        status: editTarget.status as string,
      });
  }, [editTarget?.id]);

  const { data: allDocTypes = [] } = useQuery<MasterDocType[]>({
    queryKey: ['allDocumentTypes'],
    queryFn: () => configMgmtControllerListAllDocumentTypes() as Promise<MasterDocType[]>,
  });

  const linkedIds = new Set(permitType.documentTypeLinks.map((l) => l.documentTypeId));
  const availableDocTypes = allDocTypes.filter(
    (dt) => dt.status === 'ACTIVE' && !linkedIds.has(dt.id),
  );

  const invalidate = () => qc.invalidateQueries({ queryKey: ['permitType', permitType.id] });

  const attachMutation = useMutation({
    mutationFn: (data: z.infer<typeof attachSchema>) =>
      configMgmtControllerAttachDocumentType({
        id: permitType.id,
        requestBody: {
          documentTypeId: data.documentTypeId,
          isMandatory: data.isMandatory,
          requestType: data.requestType || undefined,
        },
      }),
    onSuccess: () => {
      invalidate();
      setAddOpen(false);
      attachForm.reset();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: z.infer<typeof editLinkSchema>) =>
      configMgmtControllerUpdateDocumentLink({
        id: permitType.id,
        linkId: editTarget!.id,
        requestBody: {
          isMandatory: data.isMandatory,
          requestType: data.requestType || undefined,
          status: data.status as 'ACTIVE' | 'INACTIVE' | undefined,
        },
      }),
    onSuccess: () => {
      invalidate();
      setEditTarget(null);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (linkId: string) =>
      configMgmtControllerRemoveDocumentLink({ id: permitType.id, linkId }),
    onSuccess: () => {
      invalidate();
      setDeleteConfirm(null);
      setDeleteError(null);
    },
    onError: async (err: unknown) => {
      try {
        const body = (await (err as { response?: Response }).response?.json()) as
          | { message?: string }
          | undefined;
        setDeleteError(body?.message ?? 'Remove failed');
      } catch {
        setDeleteError('Remove failed');
      }
    },
  });

  function openEdit(link: DocumentTypeLink) {
    setEditTarget(link);
  }

  const columns: DataTableColumn<DocumentTypeLink>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (l) => <span className="text-gray-900">{l.documentType.name}</span>,
    },
    {
      key: 'code',
      header: 'Code',
      render: (l) => <span className="font-mono text-xs text-gray-600">{l.documentType.code}</span>,
    },
    {
      key: 'isMandatory',
      header: 'Mandatory',
      align: 'center',
      render: (l) =>
        l.isMandatory ? (
          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-100 text-green-600 text-xs font-bold">
            ✓
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      key: 'requestType',
      header: 'Request Type',
      render: (l) => <span className="text-xs text-gray-500">{l.requestType ?? 'All'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (l) => (
        <span
          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            l.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}
        >
          {l.status}
        </span>
      ),
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: '',
            render: (l: DocumentTypeLink) =>
              deleteConfirm === l.id ? (
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <span className="text-xs text-gray-500">Remove?</span>
                  <button
                    type="button"
                    onClick={() => removeMutation.mutate(l.id)}
                    disabled={removeMutation.isPending}
                    className="text-xs text-red-600 font-medium hover:text-red-800 disabled:opacity-50"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteConfirm(null);
                      setDeleteError(null);
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    No
                  </button>
                </div>
              ) : (
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => openEdit(l)}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteConfirm(l.id);
                      setDeleteError(null);
                    }}
                    className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ),
          } satisfies DataTableColumn<DocumentTypeLink>,
        ]
      : []),
  ];

  return (
    <>
      <DataTable
        title="Document Types"
        description="Configure required document types for this permit"
        headerActions={
          canEdit ? (
            <Button variant="primary" size="sm" onClick={() => setAddOpen(true)}>
              + Add
            </Button>
          ) : null
        }
        columns={columns}
        data={permitType.documentTypeLinks}
        keyField="id"
        emptyMessage="No document types linked"
      />

      {/* Attach dialog */}
      <Dialog
        open={addOpen}
        onOpenChange={(v) => {
          if (!v) {
            setAddOpen(false);
            attachForm.reset();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Attach Document Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={attachForm.handleSubmit((d) => attachMutation.mutate(d))}>
            <div className="space-y-4 py-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Document Type
                </label>
                <Controller
                  name="documentTypeId"
                  control={attachForm.control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select document type…" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDocTypes.map((dt) => (
                          <SelectItem key={dt.id} value={dt.id}>
                            {dt.name}{' '}
                            <span className="text-xs text-gray-400 font-mono ml-1">
                              ({dt.code})
                            </span>
                          </SelectItem>
                        ))}
                        {availableDocTypes.length === 0 && (
                          <div className="px-2 py-1.5 text-sm text-gray-400">
                            No available document types
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />
                {attachForm.formState.errors.documentTypeId && (
                  <p className="mt-1 text-xs text-error-500">
                    {attachForm.formState.errors.documentTypeId.message}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="attach-mandatory"
                  className="h-4 w-4 rounded border-gray-300 text-brand-600"
                  {...attachForm.register('isMandatory')}
                />
                <label htmlFor="attach-mandatory" className="text-sm text-gray-700 cursor-pointer">
                  Mandatory
                </label>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Request Type <span className="text-gray-400">(optional)</span>
                </label>
                <Controller
                  name="requestType"
                  control={attachForm.control}
                  render={({ field }) => (
                    <Select
                      value={field.value || 'ALL'}
                      onValueChange={(v) => field.onChange(v === 'ALL' ? '' : v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All</SelectItem>
                        <SelectItem value="NEW">New Request</SelectItem>
                        <SelectItem value="RENEWAL">Renewal Request</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => {
                  setAddOpen(false);
                  attachForm.reset();
                }}
                disabled={attachMutation.isPending}
              >
                Cancel
              </Button>
              <Button variant="primary" size="sm" type="submit" disabled={attachMutation.isPending}>
                {attachMutation.isPending ? 'Attaching…' : 'Attach'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Link — {editTarget?.documentType.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={editForm.handleSubmit((d) => updateMutation.mutate(d))}>
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="edit-mandatory"
                  className="h-4 w-4 rounded border-gray-300 text-brand-600"
                  {...editForm.register('isMandatory')}
                />
                <label htmlFor="edit-mandatory" className="text-sm text-gray-700 cursor-pointer">
                  Mandatory
                </label>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Request Type <span className="text-gray-400">(optional)</span>
                </label>
                <Controller
                  name="requestType"
                  control={editForm.control}
                  render={({ field }) => (
                    <Select
                      value={field.value || 'ALL'}
                      onValueChange={(v) => field.onChange(v === 'ALL' ? '' : v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All</SelectItem>
                        <SelectItem value="NEW">New Request</SelectItem>
                        <SelectItem value="RENEWAL">Renewal Request</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Status</label>
                <Controller
                  name="status"
                  control={editForm.control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                        <SelectItem value="INACTIVE">INACTIVE</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => setEditTarget(null)}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button variant="primary" size="sm" type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {deleteError && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm rounded-xl border border-red-200 bg-red-50 px-4 py-3 shadow-lg">
          <p className="text-sm text-red-700">{deleteError}</p>
          <button
            type="button"
            onClick={() => setDeleteError(null)}
            className="mt-1 text-xs text-red-500 hover:text-red-700"
          >
            Dismiss
          </button>
        </div>
      )}
    </>
  );
}

function DocumentsPage() {
  const { requestTypeId } = Route.useParams();
  return (
    <PermitTypeShell requestTypeId={requestTypeId}>
      {(pt) => <DocumentsTab permitType={pt} />}
    </PermitTypeShell>
  );
}
